import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AssignableRole, BASE_URL_TOKEN, UserBranchRoles } from '@raptor-fe/core';
import { Observable } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class AuthorizationService {
    private readonly http = inject(HttpClient);
    private readonly base_url = `${inject(BASE_URL_TOKEN)}v1/`;

    /**
     * Reads the current user's effective permission keys for their active branch
     * (GET v1/me/permissions). Admin and system roles project to the full catalog server-side, so a
     * privileged user simply receives every key.
     *
     * @returns The effective permission keys for the current user and branch.
     */
    getMyPermissions(tenantId: string): Observable<string[]> {
        return this.http.get<string[]>(`${this.base_url}authorization/tenant/${tenantId}/me/permissions`);
    }

    /**
     * Lists the roles a tenant may assign to a user: shared system roles plus the tenant's own
     * custom roles (GET v1/tenant/{tenantId}/role, gated by role:view). Carries the isSystem flag the
     * assignment transfer list needs to render the System vs Custom badge.
     *
     * @param tenantId The tenant whose assignable roles are requested.
     * @returns The assignable roles visible to the tenant.
     */
    getAssignableRoles(tenantId: string): Observable<AssignableRole[]> {
        return this.http.get<AssignableRole[]>(`${this.base_url}tenant/${tenantId}/role`);
    }

    /**
     * Reads a user's role assignments grouped by branch
     * (GET v1/tenant/{tenantId}/role/assignments/user/{userId}, gated by user:assignroles). The
     * response carries branchId and the role names held in that branch; assignments are per-branch.
     *
     * @param tenantId The tenant the user belongs to.
     * @param userId The id of the user whose assignments are requested.
     * @returns The user's per-branch role assignments.
     */
    getUserRoleAssignments(tenantId: string, userId: string): Observable<UserBranchRoles[]> {
        return this.http.get<UserBranchRoles[]>(`${this.base_url}tenant/${tenantId}/role/assignments/user/${userId}`);
    }

    /**
     * Replaces a user's per-branch role assignments with the supplied set
     * (PUT v1/tenant/{tenantId}/role/assignments/user/{userId}, gated by user:assignroles). Each entry
     * is the full replacement of the user's roles in that branch.
     *
     * @param tenantId The tenant the user belongs to.
     * @param userId The id of the user whose assignments are set.
     * @param assignments The complete per-branch role sets to persist.
     */
    assignUserRoles(tenantId: string, userId: string, assignments: UserBranchRoles[]): Observable<void> {
        return this.http.put<void>(`${this.base_url}tenant/${tenantId}/role/assignments/user/${userId}`, assignments);
    }
}
